/**
 * MFE parameters for a page that is not mounted as an MFE.
 *
 * `useParams()` reads the parameters of the running MFE instance, falling back
 * to the ones the host was initialised with (`window.AccessEvo.mfeHost`). A
 * static page has no MFE instance, so the values taken from its URL are
 * published on the host instead, which is the same object `useParams()` merges.
 */
import type { User } from '@access-evo/mfe-host';

export function applyHostParameters(parameters: Record<string, unknown>) {
  const mfeHost = window.AccessEvo?.mfeHost;
  if (!mfeHost) return;

  mfeHost.parameters = { ...mfeHost.parameters, ...parameters };
}

/** Decodes a JWT payload without verifying it — display purposes only. */
function decodeClaims(token: string): Record<string, string> {
  const payload = token.split('.')[1];
  if (!payload) return {};
  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(json);
}

/** Builds the Evo user from an access token's claims, for standalone runs. */
export function evoUserFromToken(token: string): User {
  const claims = decodeClaims(token);
  return {
    id: claims.acid ?? claims.sub ?? 'unknown',
    firstName: claims.given_name ?? '',
    lastName: claims.family_name ?? '',
    emailAddress: claims.email ?? 'unknown@example.com',
    organisation: { id: claims.org_id ?? 'unknown', name: claims.org_name ?? 'unknown' },
  };
}

/**
 * The app asks the Access Button for its bearer token
 * (`window.accessButton.getButtonToken`), which only the Evo/Access host
 * implements. Standalone it is answered with the supplied token, otherwise the
 * app waits for it, times out, and never calls the API.
 */
export function installButtonTokenShim(token: string) {
  window.accessButton = {
    getButtonToken: async () => ({
      status: 200,
      body: {
        access_token: token,
        token_type: 'Bearer',
        expires_in: 3600,
        expires_time: Date.now() + 3600 * 1000,
      },
    }),
  };
}
